import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { EASE } from "./Reveal";
import { ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Vārds, uzvārds", type: "text", required: true, testid: "form-name-input" },
  { name: "company", label: "Uzņēmums", type: "text", required: true, testid: "form-company-input" },
  { name: "email", label: "E-pasts", type: "email", required: true, testid: "form-email-input" },
  { name: "phone", label: "Tālrunis", type: "tel", required: false, testid: "form-phone-input" },
];

export default function AuditForm({ testidPrefix = "audit-form", dark = true }) {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, audit_types: ["Baneri", "Video"] });
      if (window.fbq) window.fbq("trackCustom", "LeadSent", { source: testidPrefix });
      if (window.gtag) window.gtag("event", "LeadSent", { source: testidPrefix });
      setDone(true);
    } catch (err) {
      toast.error("Neizdevās nosūtīt pieteikumu. Lūdzu, mēģiniet vēlreiz.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="h-full min-h-[320px] rounded-sm border border-white/15 bg-[#002B54] flex flex-col items-center justify-center text-center p-10"
        data-testid="form-success-message"
      >
        <CheckCircle2 className="h-14 w-14 text-[#ED1C27] mb-6" />
        <h3 className="font-display text-3xl font-bold tracking-tight text-white">Pieteikums saņemts!</h3>
        <p className="mt-4 text-white/55 max-w-sm">
          Paldies! Mūsu komanda izvērtēs Jūsu kreatīvus un sazināsies ar Jums tuvākajā laikā.
        </p>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-sm border border-white/15 bg-[#002B54] p-6 md:p-8"
      data-testid={testidPrefix}
    >
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
        {FIELDS.map((f) => (
          <div key={f.name}>
            <label htmlFor={`${testidPrefix}-${f.name}`} className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">
              {f.label} {f.required && <span className="text-[#ED1C27]">*</span>}
            </label>
            <input
              id={`${testidPrefix}-${f.name}`}
              data-testid={f.testid}
              type={f.type}
              required={f.required}
              value={form[f.name]}
              onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
              className="mt-1.5 w-full bg-transparent border-b-2 border-white/20 focus:border-[#ED1C27] outline-none py-2 text-white placeholder-white/25 transition-colors duration-300"
              placeholder={f.label}
            />
          </div>
        ))}
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="form-submit-button"
        className="group mt-8 w-full flex items-center justify-center gap-3 bg-[#ED1C27] text-white font-display font-bold uppercase tracking-wide px-6 py-4 rounded-full hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60"
      >
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            Saņemt bezmaksas auditu
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />
          </>
        )}
      </button>
    </form>
  );
}
